import type { PaymentPeriod } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Alert, Box, MenuItem, Select, Typography } from '@mui/material';
import { useMemo } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { paymentRhythmDisplayNames } from '../../constants';
import { getSubscription } from '../../reducers/user.selectors';
import type { ChangeRhythmPayload } from './PaymentInfo';

interface PaymentRhythmProps {
	isEditMode: boolean;
	isShowAlert: boolean;
	availablePaymentRhythm: PaymentPeriod[];
	currentPaymentRhythm: PaymentPeriod;
	control: Control<ChangeRhythmPayload>;
}
const PaymentRhythm = ({
	isEditMode,
	isShowAlert,
	availablePaymentRhythm,
	currentPaymentRhythm,
	control,
}: PaymentRhythmProps) => {
	const { t } = useTranslation();
	const subscription = useSelector(getSubscription);

	// TODO: FD-2519: remove the promoCode condition
	const canEdit = useMemo(() => {
		return (
			subscription?.status === 'Active' &&
			!subscription?.trial?.isInTrial &&
			!subscription?.paymentPeriod?.following &&
			!subscription?.promoCode
		);
	}, [
		subscription?.status,
		subscription?.trial?.isInTrial,
		subscription?.paymentPeriod.following,
		subscription?.promoCode,
	]);

	return isEditMode && canEdit ? (
		<Controller
			name="paymentRhythm"
			control={control}
			// The persisted rhythm seeds the field; react-hook-form owns the edited value.
			defaultValue={currentPaymentRhythm}
			render={({ field }) => (
				<Select
					name={field.name}
					value={field.value ?? ''}
					onBlur={field.onBlur}
					inputRef={field.ref}
					sx={{ width: '100%', height: '40px', borderRadius: borderRadius.sm }}
					onChange={field.onChange}
				>
					{availablePaymentRhythm.map((rhythm) => (
						<MenuItem key={rhythm} value={rhythm}>
							{t(paymentRhythmDisplayNames[rhythm])}
						</MenuItem>
					))}
				</Select>
			)}
		/>
	) : (
		<Box sx={{ gap: spaces.sm }}>
			<Typography>{t(paymentRhythmDisplayNames[currentPaymentRhythm as PaymentPeriod])}</Typography>
			{isShowAlert && (
				<Alert
					severity="info"
					variant="outlined"
					sx={{
						borderColor: palette.info.main,
						color: palette.info.dark,
						paddingX: spaces.md,
						paddingY: spaces.sm,
						marginTop: spaces.lg,
						fontFamily: 'Poppins',
						fontSize: '14px',
						fontStyle: 'normal',
						fontWeight: 300,
						lineHeight: '20px',
						letterSpacing: '0.25px',
					}}
				>
					{t('We have received your payment rhythm change request.')}
				</Alert>
			)}
		</Box>
	);
};

export default PaymentRhythm;
